import { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { Card } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { toast } from "sonner";
import { Wallet, CreditCard, Landmark, ArrowRightLeft, HandCoins, Pencil, Trash2, PiggyBank } from "lucide-react";

const fmt = (n) => `€ ${(n || 0).toFixed(2)}`;
const today = new Date().toISOString().slice(0, 10);
const icons = { contante: Wallet, sumup: CreditCard, banca: Landmark, deposito: PiggyBank };
const accColor = { contante: "text-emerald-500", sumup: "text-amber-500", banca: "text-sky-500", deposito: "text-violet-500" };
const ACCOUNT_OPTS = [["contante", "Contanti"], ["sumup", "SumUp"], ["banca", "Banca"], ["deposito", "Deposito"]];

export default function AccountsPanel() {
  const [accounts, setAccounts] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({ key: "", name: "", initial_balance: "" });
  const [trOpen, setTrOpen] = useState(false);
  const [trForm, setTrForm] = useState({ from_account: "contante", to_account: "banca", amount: "", date: today, note: "", label: "" });

  const load = async () => {
    const [a, t] = await Promise.all([api.get("/accounts"), api.get("/transfers").catch(() => ({ data: [] }))]);
    setAccounts(a.data); setTransfers(t.data);
  };
  useEffect(() => { load(); }, []);

  const nameOf = (k) => accounts.find((a) => a.key === k)?.name || k;
  const isWithdrawal = (t) => t.label === "Prelievo di cassa" || (t.from_account === "cassa" && t.to_account === "contante");

  const openEdit = (a) => { setEditForm({ key: a.key, name: a.name, initial_balance: String(a.initial_balance) }); setEditOpen(true); };
  const saveEdit = async () => {
    try {
      await api.put(`/accounts/${editForm.key}`, { name: editForm.name, initial_balance: parseFloat(editForm.initial_balance) || 0 });
      toast.success("Saldo iniziale aggiornato"); setEditOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openTransfer = () => { setTrForm({ from_account: "contante", to_account: "banca", amount: "", date: today, note: "", label: "" }); setTrOpen(true); };
  const openWithdrawal = () => { setTrForm({ from_account: "banca", to_account: "contante", amount: "", date: today, note: "", label: "Prelievo di cassa" }); setTrOpen(true); };

  const saveTransfer = async () => {
    if (!trForm.amount || parseFloat(trForm.amount) <= 0) return toast.error("Importo non valido");
    if (trForm.from_account === trForm.to_account) return toast.error("I conti devono essere diversi");
    try {
      await api.post("/transfers", { ...trForm, amount: parseFloat(trForm.amount) });
      toast.success(trForm.label === "Prelievo di cassa" ? "Prelievo di cassa registrato" : "Trasferimento registrato");
      setTrOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const delTransfer = async (id) => { if (!window.confirm("Eliminare questo movimento tra conti?")) return; await api.delete(`/transfers/${id}`); load(); };

  const total = accounts.reduce((s, a) => s + (a.balance || 0), 0);
  const isWd = trForm.label === "Prelievo di cassa";

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        {accounts.map((a) => {
          const Icon = icons[a.key] || Wallet;
          return (
            <Card key={a.key} className="p-5 rounded-xl" data-testid={`account-card-${a.key}`}>
              <div className="flex justify-between items-start">
                <span className="text-xs uppercase text-muted-foreground">{a.name}</span>
                <Icon className={`w-5 h-5 ${accColor[a.key] || "text-primary"}`} />
              </div>
              <p className={`font-dash text-2xl font-semibold mt-3 ${a.balance < 0 ? "text-destructive" : ""}`} data-testid={`account-balance-${a.key}`}>{fmt(a.balance)}</p>
              <div className="flex items-center justify-between mt-2">
                <span className="text-xs text-muted-foreground">iniziale {fmt(a.initial_balance)}</span>
                <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => openEdit(a)} data-testid={`edit-account-${a.key}`}><Pencil className="w-3.5 h-3.5" /></Button>
              </div>
            </Card>
          );
        })}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">Disponibilità totale: <span className="font-semibold text-foreground">{fmt(total)}</span></p>
        <div className="flex gap-2">
          <Button onClick={openWithdrawal} variant="outline" className="rounded-full" data-testid="add-withdrawal-button"><HandCoins className="w-4 h-4 mr-1" /> Prelievo di cassa</Button>
          <Button onClick={openTransfer} className="rounded-full" data-testid="add-transfer-button"><ArrowRightLeft className="w-4 h-4 mr-1" /> Trasferimento</Button>
        </div>
      </div>

      <Card className="rounded-xl overflow-hidden">
        <Table data-testid="transfers-table">
          <TableHeader><TableRow><TableHead>Data</TableHead><TableHead>Tipo</TableHead><TableHead>Da</TableHead><TableHead>A</TableHead><TableHead>Nota</TableHead><TableHead className="text-right">Importo</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {transfers.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">Nessun movimento tra conti</TableCell></TableRow>}
            {transfers.map((t) => (
              <TableRow key={t.id} data-testid={`transfer-row-${t.id}`}>
                <TableCell>{t.date}</TableCell>
                <TableCell>{isWithdrawal(t)
                  ? <span className="text-xs px-2 py-0.5 rounded-full bg-primary/15 text-primary" data-testid={`transfer-type-${t.id}`}>Prelievo</span>
                  : <span className="text-xs px-2 py-0.5 rounded-full bg-secondary text-muted-foreground">Trasferimento</span>}</TableCell>
                <TableCell>{nameOf(t.from_account)}</TableCell>
                <TableCell>{nameOf(t.to_account)}</TableCell>
                <TableCell className="text-muted-foreground">{t.note || "—"}</TableCell>
                <TableCell className="text-right font-medium">{fmt(t.amount)}</TableCell>
                <TableCell><Button size="icon" variant="ghost" onClick={() => delTransfer(t.id)} data-testid={`delete-transfer-${t.id}`}><Trash2 className="w-4 h-4 text-destructive" /></Button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent data-testid="account-edit-dialog">
          <DialogHeader><DialogTitle>Saldo iniziale · {editForm.name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Nome conto</Label><Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} data-testid="account-name-input" /></div>
            <div className="space-y-2"><Label>Saldo iniziale (€)</Label><Input type="number" step="0.01" value={editForm.initial_balance} onChange={(e) => setEditForm({ ...editForm, initial_balance: e.target.value })} data-testid="account-initial-input" /></div>
            <p className="text-xs text-muted-foreground">Saldo attuale = saldo iniziale + incassi − spese ± trasferimenti.</p>
          </div>
          <DialogFooter><Button onClick={saveEdit} className="rounded-full" data-testid="save-account-button">Salva</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={trOpen} onOpenChange={setTrOpen}>
        <DialogContent data-testid="transfer-dialog">
          <DialogHeader><DialogTitle>{isWd ? "Prelievo di cassa" : "Nuovo trasferimento"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {isWd && <p className="text-sm text-muted-foreground">Registra un <b>prelievo di cassa</b>: sposta denaro tra i tuoi conti (es. dalla <b>Banca</b> ai <b>Contanti</b>).</p>}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Da conto</Label>
                <Select value={trForm.from_account} onValueChange={(v) => setTrForm({ ...trForm, from_account: v })}>
                  <SelectTrigger data-testid="transfer-from-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{ACCOUNT_OPTS.map(([k, n]) => <SelectItem key={k} value={k}>{n}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label>A conto</Label>
                <Select value={trForm.to_account} onValueChange={(v) => setTrForm({ ...trForm, to_account: v })}>
                  <SelectTrigger data-testid="transfer-to-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{ACCOUNT_OPTS.map(([k, n]) => <SelectItem key={k} value={k}>{n}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Importo (€)</Label><Input type="number" step="0.01" value={trForm.amount} onChange={(e) => setTrForm({ ...trForm, amount: e.target.value })} data-testid="transfer-amount-input" /></div>
              <div className="space-y-2"><Label>Data</Label><Input type="date" value={trForm.date} onChange={(e) => setTrForm({ ...trForm, date: e.target.value })} data-testid="transfer-date-input" /></div>
            </div>
            <div className="space-y-2"><Label>Nota (opzionale)</Label><Input value={trForm.note} onChange={(e) => setTrForm({ ...trForm, note: e.target.value })} placeholder={isWd ? "es. Prelievo serale" : "es. Versamento SumUp in banca"} data-testid="transfer-note-input" /></div>
          </div>
          <DialogFooter><Button onClick={saveTransfer} className="rounded-full" data-testid="save-transfer-button">{isWd ? "Registra prelievo" : "Registra"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
